import { NavLink } from 'react-router-dom';
import { AppLogo } from './AppLogo';

interface LeftNavProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function LeftNav({ collapsed, onToggle }: LeftNavProps) {

  return (
    <nav className="h-full flex flex-col gap-6 px-2 py-6">
      {/* Logo / Brand */}
      <div className="flex items-center justify-between px-1">
        <div className={`flex items-center gap-2 ${collapsed ? 'justify-center w-full' : ''}`}>
          <AppLogo sizeClassName="w-8 h-8" className="rounded-btn flex-shrink-0" />
          {!collapsed && (
            <div>
              <div className="text-sm font-semibold text-neutral-800 font-heading">Athen.ai</div>
              <div className="text-xs text-neutral-500">Healthcare AI Consultant</div>
            </div>
          )}
        </div>
      </div>

      {/* Main nav items */}
      <div className="space-y-1">
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-btn text-sm font-medium transition-colors ${
              collapsed ? 'justify-center' : ''
            } ${isActive ? 'nav-item-active' : 'nav-item-inactive'}`
          }
          title={collapsed ? 'Chat' : undefined}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {!collapsed && <span>Chat</span>}
        </NavLink>

        <NavLink
          to="/upload"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-btn text-sm font-medium transition-colors ${
              collapsed ? 'justify-center' : ''
            } ${isActive ? 'nav-item-active' : 'nav-item-inactive'}`
          }
          title={collapsed ? 'Upload' : undefined}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          {!collapsed && <span>Upload</span>}
        </NavLink>

        <NavLink
          to="/assistants"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-btn text-sm font-medium transition-colors ${
              collapsed ? 'justify-center' : ''
            } ${isActive ? 'nav-item-active' : 'nav-item-inactive'}`
          }
          title={collapsed ? 'Assistants' : undefined}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
          </svg>
          {!collapsed && <span>Assistants</span>}
        </NavLink>

        <NavLink
          to="/workflows"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-btn text-sm font-medium transition-colors ${
              collapsed ? 'justify-center' : ''
            } ${isActive ? 'nav-item-active' : 'nav-item-inactive'}`
          }
          title={collapsed ? 'Workflows' : undefined}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          {!collapsed && <span>Workflows</span>}
        </NavLink>
      </div>

      {/* Bottom section */}
      <div className="mt-4 pt-4 border-t border-neutral-200 space-y-1">
        <a
          href="/#browse-tools"
          className={`flex items-center gap-3 px-3 py-2 rounded-btn text-sm font-medium transition-colors nav-item-inactive ${
            collapsed ? 'justify-center' : ''
          }`}
          title={collapsed ? 'Browse Tools' : undefined}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {!collapsed && <span>Browse Tools</span>}
        </a>
        <button
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-btn text-sm font-medium transition-colors nav-item-inactive ${
            collapsed ? 'justify-center' : ''
          }`}
          type="button"
          title={collapsed ? 'Settings Coming Soon' : undefined}
          onClick={() => {
            const el = document.getElementById('settings-toast');
            if (el) { el.remove(); }
            const toast = document.createElement('div');
            toast.id = 'settings-toast';
            toast.className = 'fixed bottom-6 left-1/2 -translate-x-1/2 bg-neutral-800 text-white text-sm px-4 py-2 rounded-btn shadow-lg z-50 transition-opacity duration-300';
            toast.textContent = 'Settings Coming Soon';
            document.body.appendChild(toast);
            setTimeout(() => { toast.style.opacity = '0'; setTimeout(() => toast.remove(), 300); }, 2000);
          }}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {!collapsed && <span>Settings</span>}
        </button>
      </div>

      {/* Collapse toggle at the bottom */}
      <div className="mt-auto pt-2">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-btn text-sm font-medium transition-colors nav-item-inactive"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
          {!collapsed && <span className="text-xs text-neutral-500">Collapse</span>}
        </button>
      </div>
    </nav>
  );
}
